/**
 * Pure formatting/derivation helpers for the Dashboard Outcomes panel.
 * Split out of the component so they can be unit-tested without the DOM.
 */

/** Plain-language labels for `audit_log.user_action` exit reasons. */
export const EXIT_ACTION_LABELS: Record<string, string> = {
  stop_exit: 'Stop hit',
  force_exit_max_hold: 'Time-limit exit',
  force_exit_max_loss: 'Max-loss backstop',
  quick_profit_exit: 'Quick profit',
  rotation_exit: 'Rotation',
  tp_exit: 'Take profit',
  tp1_partial: 'Partial take-profit',
  engine_exit: 'Engine exit',
  engine_reduce: 'Engine reduce',
  admin_close: 'Manual close',
};

export function exitActionLabel(action: string): string {
  return EXIT_ACTION_LABELS[action] ?? action.replace(/_/g, ' ');
}

export function formatGbp(value: number): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value);
}

/** Signed GBP: +£12.40 / -£8.10 (Intl already prefixes the minus). */
export function formatSignedGbp(value: number): string {
  return `${value > 0 ? '+' : ''}${formatGbp(value)}`;
}

export type PayoffTone = 'success' | 'error' | 'neutral';

/**
 * The honest payoff signal: red when the payoff ratio is below what the hit
 * rate needs to break even, green when at/above it, neutral when unknowable.
 */
export function payoffAssessment(
  payoffRatio: number | null,
  breakEvenPayoff: number | null,
): { tone: PayoffTone; label: string } {
  if (payoffRatio === null || breakEvenPayoff === null) {
    return { tone: 'neutral', label: 'Payoff n/a' };
  }
  const label = `Payoff ${payoffRatio.toFixed(2)} vs ${breakEvenPayoff.toFixed(2)} needed`;
  return { tone: payoffRatio >= breakEvenPayoff ? 'success' : 'error', label };
}

/** Human hold-time from 5-min candle count: 7 → "35m", 78 → "6.5h". */
export function holdLabel(holdCandles: number | null): string | null {
  if (holdCandles === null) return null;
  const mins = holdCandles * 5;
  if (mins < 60) return `${mins}m`;
  const hrs = mins / 60;
  if (hrs < 24) return `${hrs % 1 === 0 ? hrs : hrs.toFixed(1)}h`;
  return `${(hrs / 24).toFixed(1)}d`;
}

/** Honest snapshot-staleness label for the Open Positions card. */
export function snapshotLabel(fetchedAt: number | null, now: number = Date.now()): string {
  if (fetchedAt === null) {
    return 'No recent snapshot — positions refresh during market hours';
  }
  const time = new Date(fetchedAt).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const ageMin = Math.max(0, Math.floor((now - fetchedAt) / 60000));
  const age = ageMin < 1 ? 'just now' : ageMin < 60 ? `${ageMin}m old` : `${Math.floor(ageMin / 60)}h old`;
  return `Snapshot from ${time} (${age})`;
}

/**
 * SVG polyline geometry for the cumulative-P&L sparkline.
 * Returns the point list plus the y of the zero line (null when zero is out
 * of range). Pure so the maths is testable.
 */
export function buildSparkline(
  values: number[],
  width: number,
  height: number,
  pad = 2,
): { points: string; zeroY: number | null } | null {
  if (values.length === 0) return null;

  const min = Math.min(0, ...values);
  const max = Math.max(0, ...values);
  const span = max - min || 1;
  const innerW = width - pad * 2;
  const innerH = height - pad * 2;

  const y = (v: number) => pad + innerH - ((v - min) / span) * innerH;
  const pts =
    values.length === 1
      ? [`${pad},${y(values[0]).toFixed(1)}`, `${width - pad},${y(values[0]).toFixed(1)}`]
      : values.map((v, i) => {
          const x = pad + (i / (values.length - 1)) * innerW;
          return `${x.toFixed(1)},${y(v).toFixed(1)}`;
        });

  const zeroY = min <= 0 && max >= 0 ? y(0) : null;
  return { points: pts.join(' '), zeroY: zeroY !== null ? Math.round(zeroY * 10) / 10 : null };
}
